import React from "react";
import { Link } from "react-router-dom";
import {
  CreditCard,
  Shield,
  Smartphone,
  Globe,
  HelpCircle,
  ChevronRight,
  ChevronDown,
  Check,
  Users,
  Lock,
  Zap,
  TrendingUp,
  ArrowRight,
  UserPlus,
  FileText,
  CheckCircle2,
  CreditCard as CardIcon,
  Wallet,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

const Guide = () => {
  const features = [
    {
      icon: <CreditCard className="h-8 w-8 text-blue-500" />,
      title: "Global Payment Card",
      description:
        "Use your Mastercard for online and offline payments anywhere in the world.",
    },
    {
      icon: <Shield className="h-8 w-8 text-green-500" />,
      title: "Secure Transactions",
      description:
        "Protected by advanced security technology and 3D Secure authentication.",
    },
    {
      icon: <Smartphone className="h-8 w-8 text-purple-500" />,
      title: "Mobile Optimized",
      description:
        "Manage your card and check transaction history anytime, anywhere on your smartphone.",
    },
    {
      icon: <Globe className="h-8 w-8 text-orange-500" />,
      title: "Multi-Currency Support",
      description:
        "Top up and use your card with various currencies including USD and EUR.",
    },
    {
      icon: <Zap className="h-8 w-8 text-yellow-500" />,
      title: "Instant Issuance",
      description:
        "Get your card quickly without complicated paperwork and start using it immediately.",
    },
    {
      icon: <TrendingUp className="h-8 w-8 text-red-500" />,
      title: "Real-time Management",
      description:
        "Check balances, view transaction history, and manage card status in real-time.",
    },
  ];

  const steps = [
    {
      step: 1,
      icon: <UserPlus className="h-6 w-6" />,
      title: "Platform Sign Up",
      description: "Create your account with email and password.",
      details: [
        "Enter your email address",
        "Enter your first and last name",
        "Set your password",
        "Re-enter password for confirmation",
        "Click Sign Up to complete registration",
      ],
    },
    {
      step: 2,
      icon: <FileText className="h-6 w-6" />,
      title: "KYC Verification",
      description: "Complete identity verification through KYC process.",
      details: [
        "Click KYC Verification button",
        "Select your country",
        "Upload passport or ID card photo",
        "Enter phone number and address",
        "Verification complete",
      ],
    },
    {
      step: 3,
      icon: <CardIcon className="h-6 w-6" />,
      title: "Purchase Card",
      description: "Select and purchase your preferred card type.",
      details: [
        "Choose card type",
        "Enter shipping information",
        "Complete purchase on Node platform",
      ],
    },
    {
      step: 4,
      icon: <Wallet className="h-6 w-6" />,
      title: "Card Activation",
      description: "Activate your card and set PIN.",
      details: [
        "Click Card Activation button",
        "Enter 16-digit card number",
        "Enter expiry date and CVC",
        "Set your PIN number",
        "Card activated",
      ],
    },
    {
      step: 5,
      icon: <CheckCircle2 className="h-6 w-6" />,
      title: "Start Using",
      description: "Your card is ready for global payments!",
      details: [
        "Online shopping",
        "In-store payments",
        "ATM withdrawals",
        "View transaction history",
      ],
    },
  ];

  const faqs = [
    {
      question: "What is FellasCard?",
      answer:
        "FellasCard is a global prepaid Mastercard service. You can easily get a card without complicated bank account opening and use it anywhere in the world.",
    },
    {
      question: "How long does card issuance take?",
      answer:
        "Virtual cards are issued immediately after KYC verification. Physical cards are delivered within 5-10 business days after application.",
    },
    {
      question: "What currencies are supported?",
      answer:
        "We currently support major currencies including USD, EUR, and GBP, and are continuously expanding our supported currencies.",
    },
    {
      question: "How do I top up my card?",
      answer:
        "You can top up using various methods including cryptocurrency, bank transfer, and other cards. The topped-up amount is immediately available for use.",
    },
    {
      question: "How can I manage my card status?",
      answer:
        "Click the Card button on the platform home, select your card, and choose activate or lock/unlock. Payments are not supported when card status is 'Locked'.",
    },
    {
      question: "Is it secure?",
      answer:
        "We ensure safe transactions with multi-layered security systems including 3D Secure, real-time transaction alerts, and card lock features.",
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white dark:from-gray-900 dark:to-gray-800">
      {/* Hero Section */}
      <section className="relative overflow-hidden py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 dark:text-white mb-6">
              FellasCard Complete Guide
            </h1>
            <p className="text-xl text-gray-600 dark:text-gray-300 mb-8 max-w-3xl mx-auto">
              The new standard for global payments. Travel the world with
              FellasCard. Learn how to get started easily without complicated
              procedures.
            </p>
            <div className="flex gap-4 justify-center">
              <Link to="/signup">
                <Button size="lg" className="text-lg px-8">
                  Get Started <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/login">
                <Button size="lg" variant="outline" className="text-lg px-8">
                  Login
                </Button>
              </Link>
            </div>
          </div>
        </div>

        {/* Background decoration */}
        {/* <div className="absolute top-10 left-10 w-72 h-72 bg-blue-300 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob"></div>
        <div className="absolute top-10 right-10 w-72 h-72 bg-purple-300 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-2000"></div>
        <div className="absolute -bottom-8 left-20 w-72 h-72 bg-pink-300 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-4000"></div> */}
      </section>

      {/* Features Section */}
      <section className="py-16 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Why FellasCard?
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-300">
              Discover the unique features of FellasCard
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <Card
                key={index}
                className="hover:shadow-lg transition-shadow duration-300"
              >
                <CardHeader>
                  <div className="mb-4">{feature.icon}</div>
                  <CardTitle className="text-xl">{feature.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-base">
                    {feature.description}
                  </CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-16 px-6 bg-gray-50 dark:bg-gray-900">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              How to Get Started
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-300">
              Complete your global payment card in 5 steps
            </p>
          </div>

          <div className="space-y-8 max-w-4xl mx-auto">
            {steps.map((step, index) => (
              <div key={index} className="relative">
                <div className="flex gap-8 items-start">
                  <div className="flex-shrink-0 flex flex-col items-center">
                    <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg shadow-lg">
                      {step.step}
                    </div>
                    {index < steps.length - 1 && (
                      <div className="mt-4 flex flex-col items-center">
                        <div className="h-16 w-0.5 bg-gradient-to-b from-blue-400 to-blue-200 dark:from-blue-600 dark:to-blue-800"></div>
                        <ChevronDown className="h-5 w-5 text-blue-400 dark:text-blue-600 -mt-1" />
                      </div>
                    )}
                  </div>

                  <Card className="w-full max-w-2xl hover:shadow-lg transition-shadow duration-300">
                    <CardHeader className="text-center">
                      <div className="flex justify-center mb-3">
                        <div className="p-3 bg-blue-100 dark:bg-blue-900 rounded-lg">
                          {step.icon}
                        </div>
                      </div>
                      <CardTitle className="text-xl mb-2">
                        {step.title}
                      </CardTitle>
                      <CardDescription className="text-base">
                        {step.description}
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-2.5 max-w-sm mx-auto">
                        {step.details.map((detail, idx) => (
                          <li
                            key={idx}
                            className="flex items-start gap-3 text-gray-600 dark:text-gray-300"
                          >
                            <Check className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                            <span className="text-sm">{detail}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-300">
              Quick answers to common questions
            </p>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <Card key={index}>
                <CardHeader>
                  <CardTitle className="flex items-start gap-3">
                    <HelpCircle className="h-6 w-6 text-blue-500 flex-shrink-0 mt-0.5" />
                    <span>{faq.question}</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 dark:text-gray-300 ml-9">
                    {faq.answer}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 bg-gray-50 dark:bg-gray-900">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-300 mb-8">
            Start your FellasCard journey now and experience the freedom of
            global payments
          </p>
          <div className="flex gap-4 justify-center">
            <Link to="/signup">
              <Button size="lg" className="text-lg px-4">
                Get Started Free <ChevronRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/help">
              <Button size="lg" variant="outline" className="text-lg px-4">
                <HelpCircle className="mr-2 h-5 w-5" />
                Help Center
              </Button>
            </Link>
          </div>

          <div className="mt-12 flex items-center justify-center gap-8 text-gray-600 dark:text-gray-400">
            <div className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              <span>10,000+ Users</span>
            </div>
            <div className="flex items-center gap-2">
              <Globe className="h-5 w-5" />
              <span>150+ Countries</span>
            </div>
            <div className="flex items-center gap-2">
              <Lock className="h-5 w-5" />
              <span>Secure Platform</span>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
        <div className="max-w-6xl mx-auto text-center">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            © {new Date().getFullYear()} Fellas Corporation. All rights
            reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Guide;
